// src/components/PasswordStrengthMeter.jsx
// Password strength indicator with real-time validation feedback

import React, { useMemo } from 'react';
import { Check, X, AlertCircle } from 'lucide-react';

// Password requirements (should match backend)
const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true
};

// Check individual requirements
function checkRequirements(password) {
  return {
    length: password.length >= PASSWORD_REQUIREMENTS.minLength,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
    noRepeated: !/(.)\1{3,}/.test(password),
    noSequential: !/(?:0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|defg|efgh|fghi|ghij|hijk|ijkl|jklm|klmn|lmno|mnop|nopq|opqr|pqrs|qrst|rstu|stuv|tuvw|uvwx|vwxy|wxyz)/i.test(password)
  };
}

// Calculate strength score (0-100)
function calculateStrength(password, checks) {
  if (!password) return 0;

  let score = 0;
  const totalChecks = 7;

  // Add points for each passed check
  if (checks.length) score += 100 / totalChecks;
  if (checks.uppercase) score += 100 / totalChecks;
  if (checks.lowercase) score += 100 / totalChecks;
  if (checks.number) score += 100 / totalChecks;
  if (checks.special) score += 100 / totalChecks;
  if (checks.noRepeated) score += 100 / totalChecks;
  if (checks.noSequential) score += 100 / totalChecks;

  // Bonus for extra length
  if (password.length > 16) score = Math.min(100, score + 5);
  if (password.length > 20) score = Math.min(100, score + 5);

  return Math.round(score);
}

// Get strength label and color
function getStrengthInfo(score) {
  if (score < 30) return { label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600' };
  if (score < 60) return { label: 'Fair', color: 'bg-orange-500', textColor: 'text-orange-600' };
  if (score < 80) return { label: 'Good', color: 'bg-yellow-500', textColor: 'text-yellow-600' };
  if (score < 100) return { label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600' };
  return { label: 'Excellent', color: 'bg-green-600', textColor: 'text-green-700' };
}

const PasswordStrengthMeter = ({
  password = '',
  email = '',
  showRequirements = true,
  compact = false,
  className = ''
}) => {
  const checks = useMemo(() => checkRequirements(password), [password]);
  const strength = useMemo(() => calculateStrength(password, checks), [password, checks]);
  const strengthInfo = useMemo(() => getStrengthInfo(strength), [strength]);

  // Check if password contains email
  const containsEmail = useMemo(() => {
    if (!email || !password) return false;
    const emailPrefix = email.split('@')[0].toLowerCase();
    return emailPrefix.length > 3 && password.toLowerCase().includes(emailPrefix);
  }, [password, email]);

  // All checks pass?
  const allPassed = checks.length && checks.uppercase && checks.lowercase &&
    checks.number && checks.special && checks.noRepeated && checks.noSequential && !containsEmail;

  if (!password) {
    return null;
  }

  const RequirementItem = ({ met, label }) => (
    <div className={`flex items-center gap-2 text-sm ${met ? 'text-green-600' : 'text-gray-500'}`}>
      {met ? (
        <Check size={14} className="text-green-600" />
      ) : (
        <X size={14} className="text-gray-400" />
      )}
      <span>{label}</span>
    </div>
  );

  if (compact) {
    return (
      <div className={`space-y-1 ${className}`}>
        {/* Strength bar */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${strengthInfo.color}`}
              style={{ width: `${strength}%` }}
            />
          </div>
          <span className={`text-xs font-medium ${strengthInfo.textColor}`}>
            {strengthInfo.label}
          </span>
        </div>

        {/* Warning for failing checks */}
        {!allPassed && (
          <p className="text-xs text-amber-600 flex items-center gap-1">
            <AlertCircle size={12} />
            Password doesn't meet all requirements
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Password strength</span>
          <span className={`text-sm font-medium ${strengthInfo.textColor}`}>
            {strengthInfo.label}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${strengthInfo.color}`}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Requirements list */}
      {showRequirements && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-1.5">
          <p className="text-xs font-semibold text-gray-700 mb-2">Password must have:</p>
          <RequirementItem met={checks.length} label={`At least ${PASSWORD_REQUIREMENTS.minLength} characters`} />
          <RequirementItem met={checks.uppercase} label="At least one uppercase letter (A-Z)" />
          <RequirementItem met={checks.lowercase} label="At least one lowercase letter (a-z)" />
          <RequirementItem met={checks.number} label="At least one number (0-9)" />
          <RequirementItem met={checks.special} label="At least one special character (!@#$%...)" />
          <RequirementItem met={checks.noRepeated} label="No repeated characters (aaaa)" />
          <RequirementItem met={checks.noSequential} label="No sequential characters (1234, abcd)" />
          {email && (
            <RequirementItem met={!containsEmail} label="Does not contain your email" />
          )}
        </div>
      )}
    </div>
  );
};

// Export validation function for use in forms
export function validatePasswordStrength(password, email = null) {
  const checks = checkRequirements(password);

  const errors = [];

  if (!checks.length) errors.push(`at least ${PASSWORD_REQUIREMENTS.minLength} characters`);
  if (!checks.uppercase) errors.push('an uppercase letter');
  if (!checks.lowercase) errors.push('a lowercase letter');
  if (!checks.number) errors.push('a number');
  if (!checks.special) errors.push('a special character');

  if (!checks.noRepeated) {
    return 'Password cannot contain 4 or more repeated characters';
  }

  if (!checks.noSequential) {
    return 'Password cannot contain sequential characters (like 1234 or abcd)';
  }

  if (email) {
    const emailPrefix = email.split('@')[0].toLowerCase();
    if (emailPrefix.length > 3 && password.toLowerCase().includes(emailPrefix)) {
      return 'Password cannot contain your email address';
    }
  }

  if (errors.length > 0) {
    return `Password must contain ${errors.join(', ')}`;
  }

  return null; // Valid
}

export default PasswordStrengthMeter;
